import { useMemo } from 'react';
import { PermissionGroupConfig, getCustomGroup } from '../config/permissionGroups';
import { RolePermission } from '../models/Roles';

export interface GroupedPermission {
  group: string;
  permissions: RolePermission[];
}

/**
 * Agrupa los permisos usando grupos personalizados primero, luego por prefijo
 * Ejemplo: 'cod-payments.files.view' -> grupo 'cod-payments'
 * 'bank-accounts.verify' -> grupo 'Billeteras'
 * 
 * @param permissions Lista de permisos a agrupar
 * @param customGroupMap Mapa de grupos personalizados a usar
 * @returns Array de permisos agrupados
 */
export const groupPermissionsByPath = (
  permissions: RolePermission[],
  customGroupMap: PermissionGroupConfig
): GroupedPermission[] => {
  const groupsMap = new Map<string, RolePermission[]>();

  // Validar que permissions sea un array válido
  if (!Array.isArray(permissions) || permissions.length === 0) {
    return [];
  }

  permissions.forEach((permission) => {
    // Validar que el permiso tenga slug válido
    if (!permission || !permission.slug || typeof permission.slug !== 'string') {
      return; // Saltar permisos inválidos
    }

    // Intentar obtener grupo personalizado primero
    const customGroup = getCustomGroup(permission.slug, customGroupMap);
    let group: string;

    if (customGroup) {
      group = customGroup;
    } else {
      // Si no hay grupo personalizado, usar el primer nivel de la ruta
      const firstDotIndex = permission.slug.indexOf('.');
      group = firstDotIndex !== -1 ? permission.slug.substring(0, firstDotIndex) : permission.slug;
    }

    if (!groupsMap.has(group)) {
      groupsMap.set(group, []);
    }
    groupsMap.get(group)!.push(permission);
  });

  // Convertir a array y ordenar alfabéticamente por grupo
  // Los grupos personalizados aparecen primero
  return Array.from(groupsMap.entries())
    .map(([group, permissions]) => ({
      group,
      permissions: permissions, // Mostrar todos los permisos, incluyendo .view
    }))
    .sort((a, b) => {
      // Ordenar: grupos personalizados primero, luego alfabéticamente
      const aIsCustom = Object.keys(customGroupMap).includes(a.group);
      const bIsCustom = Object.keys(customGroupMap).includes(b.group);

      if (aIsCustom && !bIsCustom) return -1;
      if (!aIsCustom && bIsCustom) return 1;

      return a.group.localeCompare(b.group);
    });
};

/**
 * Hook para agrupar permisos usando configuración personalizada
 * @param permissions Lista de permisos a agrupar
 * @param customGroupMap Mapa de grupos personalizados a usar
 * @returns Permisos agrupados
 */
export const usePermissionGroups = (
  permissions: RolePermission[],
  customGroupMap: PermissionGroupConfig
): GroupedPermission[] => {
  return useMemo(() => {
    return groupPermissionsByPath(permissions, customGroupMap);
  }, [permissions, customGroupMap]);
};


